import React from 'react';
import { X, Play, MessageSquare, Link2, ArrowLeftRight, ArrowRightLeft, Image, Sparkles, Zap } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  cardName: string;
  onClose: () => void;
  onFlashcard: () => void;
  onGeneratePhrases: () => void;
  onGenerateCollocations: () => void;
  onGenerateSynonyms: () => void;
  onGenerateAntonyms: () => void;
  onGenerateImage: () => void;
  onUpdateCardAI?: () => void;
  onContextual?: () => void;
}

export const CardPracticePickerModal: React.FC<Props> = ({
  cardName,
  onClose,
  onFlashcard,
  onGeneratePhrases,
  onGenerateCollocations,
  onGenerateSynonyms,
  onGenerateAntonyms,
  onGenerateImage,
  onUpdateCardAI,
  onContextual,
}) => {
  useBodyScrollLock();
  const handlePick = (fn: () => void) => {
    onClose();
    fn();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 truncate">{cardName}</h2>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 flex-shrink-0 ml-2">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 sm:px-5 pb-6 pt-4 space-y-5">

          {/* Practice section */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Practice</p>
            <button
              type="button"
              onClick={() => handlePick(onFlashcard)}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <span className="flex-shrink-0 p-2 rounded-lg bg-green-100 text-green-600">
                <Play className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900">Flashcard</p>
                <p className="text-xs text-gray-500 mt-0.5">Review this card and rate how well you know it</p>
              </div>
            </button>

            {onContextual && (
              <button
                type="button"
                onClick={() => handlePick(onContextual)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-purple-100 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
              >
                <span className="flex-shrink-0 p-2 rounded-lg bg-purple-100 text-purple-600">
                  <Zap className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Q&amp;A Practice</p>
                  <p className="text-xs text-gray-500 mt-0.5">Answer an AI-generated question using this word</p>
                </div>
              </button>
            )}
          </div>

          {/* Explore section — 2-column grid */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Explore</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handlePick(onGeneratePhrases)}
                className="flex flex-col gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="p-2 rounded-lg bg-blue-100 text-blue-600 self-start">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Phrases</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">Example sentences</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handlePick(onGenerateCollocations)}
                className="flex flex-col gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="p-2 rounded-lg bg-indigo-100 text-indigo-600 self-start">
                  <Link2 className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Collocations</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">Word combinations</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handlePick(onGenerateSynonyms)}
                className="flex flex-col gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="p-2 rounded-lg bg-teal-100 text-teal-600 self-start">
                  <ArrowLeftRight className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Synonyms</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">Similar meaning</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handlePick(onGenerateAntonyms)}
                className="flex flex-col gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="p-2 rounded-lg bg-orange-100 text-orange-600 self-start">
                  <ArrowRightLeft className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Antonyms</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">Opposite meaning</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handlePick(onGenerateImage)}
                className="flex flex-col gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="p-2 rounded-lg bg-rose-100 text-rose-600 self-start">
                  <Image className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Image</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">AI illustration</p>
                </div>
              </button>
            </div>
          </div>

          {/* Add content section */}
          {onUpdateCardAI && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Add content</p>
              <button
                type="button"
                onClick={() => handlePick(onUpdateCardAI)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-purple-100 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
              >
                <span className="flex-shrink-0 p-2 rounded-lg bg-purple-100 text-purple-600">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Update card with AI</p>
                  <p className="text-xs text-gray-500 mt-0.5">Enrich this card using AI</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
